import { OTA_CHUNK_SIZE } from '../constants/deviceConfig';
import { getSensorMqttKey, getActionPinMqttKey } from './deviceIds';

// PubSubClient defaults to a 256-byte buffer — too small for base64 OTA chunks.
const MQTT_RX_BUFFER_SIZE = Math.max(8192, Math.ceil((OTA_CHUNK_SIZE * 4) / 3) + 512);

export function generateSketch({
  sensors,
  actionPins,
  automations,
  userId,
  deviceId,
  wifiSsid,
  wifiPassword,
  deviceSecret = '',
  mqttServer = '46.202.88.29',
  mqttPort = 1883,
}) {
  // Minimal includes - only what's actually used
  let includes = `
#include <WiFi.h>
#include <PubSubClient.h>
#include <ArduinoJson.h>
#include <Preferences.h>
#include <Update.h>`;

  const hasDHT = sensors.some(s => s.sensorType === 'DHT11' || s.sensorType === 'DHT22');
  const hasLEDStrip = actionPins.some(ap => ap.type === 'light' && ap.lightType === '3 Lead LED Strip');
  const hasTimeBasedAutomations = automations.some(a => a.trigger.type === 'time');

  if (hasDHT) includes += '\n#include <DHT.h>';
  if (hasLEDStrip) includes += '\n#include <FastLED.h>';
  if (hasTimeBasedAutomations) includes += '\n#include <NTPClient.h>\n#include <WiFiUdp.h>';

  // Generate sensor-specific variables and setup
  const sensorVars = [];
  const sensorSetup = [];

  // Generate LED strip variables and setup
  const ledStripVars = [];
  const ledStripSetup = [];
  if (hasLEDStrip) {
    let ledStripIndex = 0;
    actionPins.forEach((ap) => {
      if (ap.type === 'light' && ap.lightType === '3 Lead LED Strip') {
        ledStripVars.push(`CRGB leds${ledStripIndex}[${ap.numLights}];`);
        ledStripSetup.push(`FastLED.addLeds<WS2811, ${ap.pin}>(leds${ledStripIndex}, ${ap.numLights});`);
        ledStripIndex++;
      }
    });
  }

  // Generate sensor-specific reads with error handling
  const sensorReads = [];
  const emergencyVars = [];
  const emergencyLogic = [];

  // Generate NTP variables and setup
  const ntpVars = [];
  const ntpSetup = [];
  if (hasTimeBasedAutomations) {
    ntpVars.push(`WiFiUDP ntpUDP;
NTPClient timeClient(ntpUDP);`);
    ntpSetup.push(`timeClient.begin();
  timeClient.setTimeOffset(-21600); // CST timezone offset`);
  }

  // Collect unique DHT configurations per pin
  const dhtConfigs = {};
  sensors.forEach(sensor => {
    if (sensor.sensorType === 'DHT11' || sensor.sensorType === 'DHT22') {
      if (!dhtConfigs[sensor.pin]) {
        dhtConfigs[sensor.pin] = sensor.sensorType;
      }
    }
  });

  // Create DHT objects for unique pins
  const dhtPinArray = Object.keys(dhtConfigs);
  dhtPinArray.forEach((pin, idx) => {
    sensorVars.push(`DHT dht${idx}(${pin}, ${dhtConfigs[pin]});`);
    sensorSetup.push(`dht${idx}.begin();`);
  });

  // Map pin to DHT index
  const pinToDhtIdx = {};
  dhtPinArray.forEach((pin, idx) => pinToDhtIdx[pin] = idx);

  sensors.forEach((sensor, index) => {
    switch (sensor.sensorType) {
      case 'DHT11':
      case 'DHT22':
        // DHT objects already created above
        break;
      case 'soil_moisture':
        // Soil moisture typically uses analog read, no special setup needed
        break;
      default:
        // Generic analog sensor
        break;
    }
  });



  // Group sensors by type for incrementing vars
  const sensorGroups = {};
  sensors.forEach((s, idx) => {
    const key = s.dataType || s.sensorType;
    if (!sensorGroups[key]) sensorGroups[key] = [];
    sensorGroups[key].push({ ...s, groupIdx: sensorGroups[key].length });
  });

  // Check if we have soil moisture sensors and water pumps
  const hasSoilMoisture = sensors.some(s => s.sensorType === 'soil_moisture');
  const hasPumps = actionPins.some(ap => ap.type === 'water_pump');

  // Always declare safeguards variables since callback function uses them
  emergencyVars.push(`// Safeguards variables
bool safeguardsActive = false;
bool lastSafeguardsState = false;
unsigned long pumpActivatedTime = 0;
float lastMoistureReading = -1.0;
float previousMoistureReading = -1.0;
unsigned long lastReadingTime = 0;`);

  const wifiSetup = `
char* ssid = "${wifiSsid}";
char* password = "${wifiPassword}";
const char* mqtt_server = "${mqttServer}";
const int   mqtt_port   = ${mqttPort};

WiFiClient espClient;
PubSubClient client(espClient);

unsigned long long lastUpdateTime = 0;

// Preferences for calibration storage
Preferences preferences;
${sensorVars.join('\n')}
${ledStripVars.join('\n')}
${ntpVars.join('\n')}
${emergencyVars.join('\n')}

// Active state variables for automations
${actionPins.map((ap, idx) => `bool ${ap.name.toLowerCase().replace(/\s+/g, '_')} = false;`).join('\n')}

const size_t OTA_CHUNK_SIZE = ${OTA_CHUNK_SIZE};
#define MQTT_RX_BUFFER_SIZE ${MQTT_RX_BUFFER_SIZE}
bool otaActive = false;
char otaSessionId[33] = "";
size_t otaFirmwareSize = 0;
uint16_t otaTotalChunks = 0;
uint16_t otaNextChunkIndex = 0;
size_t otaWrittenBytes = 0;

void publishOtaStatus(const char* sessionId, const char* status, int progress = -1, const char* message = nullptr) {
  StaticJsonDocument<384> doc;
  doc["userId"] = "${userId}";
  doc["deviceId"] = ${deviceId};
  doc["deviceSecret"] = "${deviceSecret}";
  doc["ota_status"] = true;
  doc["sessionId"] = sessionId;
  doc["status"] = status;
  if (progress >= 0) doc["progress"] = progress;
  if (message) doc["message"] = message;
  char buffer[384];
  serializeJson(doc, buffer);
  if (!client.publish("devices/${userId}/${deviceId}/data", buffer)) {
    Serial.println("[OTA] publish status failed (MQTT buffer full?)");
  }
}

void logOtaUpdateError(const char* step) {
  Serial.printf("[OTA] %s failed, Update error=%d\\n", step, Update.getError());
}

bool decodeBase64Chunk(const char* input, uint8_t* output, size_t maxOutput, size_t* outputLen) {
  size_t inputLen = strlen(input);
  size_t written = 0;
  int val = 0;
  int valb = -8;

  for (size_t i = 0; i < inputLen; i++) {
    unsigned char c = input[i];
    if (c == '=') break;
    if ((c < 'A' || c > 'Z') && (c < 'a' || c > 'z') && (c < '0' || c > '9') && c != '+' && c != '/') continue;

    int decodedChar;
    if (c >= 'A' && c <= 'Z') decodedChar = c - 'A';
    else if (c >= 'a' && c <= 'z') decodedChar = c - 'a' + 26;
    else if (c >= '0' && c <= '9') decodedChar = c - '0' + 52;
    else if (c == '+') decodedChar = 62;
    else if (c == '/') decodedChar = 63;
    else continue;

    val = (val << 6) + decodedChar;
    valb += 6;

    if (valb >= 0) {
      if (written >= maxOutput) return false;
      output[written++] = (val >> valb) & 0xFF;
      valb -= 8;
    }
  }

  *outputLen = written;
  return true;
}

void resetOtaSession() {
  otaActive = false;
  otaSessionId[0] = '\\0';
  otaFirmwareSize = 0;
  otaTotalChunks = 0;
  otaNextChunkIndex = 0;
  otaWrittenBytes = 0;
}

void handleOtaBegin(const char* sessionId, size_t firmwareSize, uint16_t totalChunks) {
  Serial.println("[OTA] ota_begin received");
  if (otaActive) {
    Serial.println("[OTA] aborting previous session");
    Update.abort();
    resetOtaSession();
  }

  if (!sessionId || firmwareSize == 0 || totalChunks == 0) {
    Serial.println("[OTA] invalid begin payload");
    publishOtaStatus(sessionId ? sessionId : "", "error", -1, "Invalid OTA begin payload");
    return;
  }

  otaActive = true;
  strncpy(otaSessionId, sessionId, 32);
  otaSessionId[32] = '\\0';
  otaFirmwareSize = firmwareSize;
  otaTotalChunks = totalChunks;
  otaNextChunkIndex = 0;
  otaWrittenBytes = 0;

  Serial.printf("[OTA] session=%s size=%u chunks=%u\\n", otaSessionId, (unsigned)firmwareSize, totalChunks);

  if (!Update.begin(firmwareSize, U_FLASH)) {
    logOtaUpdateError("Update.begin");
    publishOtaStatus(sessionId, "error", -1, "Update.begin failed");
    Update.abort();
    resetOtaSession();
    return;
  }

  Serial.println("[OTA] Update.begin OK, publishing ready");
  publishOtaStatus(sessionId, "ready", 0);
}

void handleOtaChunk(const char* sessionId, uint16_t index, const char* dataB64) {
  if (!otaActive || !sessionId || strcmp(sessionId, otaSessionId) != 0) {
    Serial.println("[OTA] chunk ignored (inactive or session mismatch)");
    return;
  }

  if (index != otaNextChunkIndex) {
    Serial.printf("[OTA] unexpected chunk index got=%u expected=%u\\n", index, otaNextChunkIndex);
    publishOtaStatus(sessionId, "error", -1, "Unexpected chunk index");
    Update.abort();
    resetOtaSession();
    return;
  }

  uint8_t chunkBuf[OTA_CHUNK_SIZE];
  size_t decodedLen = 0;
  if (!dataB64 || !decodeBase64Chunk(dataB64, chunkBuf, sizeof(chunkBuf), &decodedLen)) {
    Serial.println("[OTA] base64 decode failed");
    publishOtaStatus(sessionId, "error", -1, "Base64 decode failed");
    Update.abort();
    resetOtaSession();
    return;
  }

  if (Update.write(chunkBuf, decodedLen) != decodedLen) {
    logOtaUpdateError("Update.write");
    publishOtaStatus(sessionId, "error", -1, "Update.write failed");
    Update.abort();
    resetOtaSession();
    return;
  }

  otaNextChunkIndex++;
  otaWrittenBytes += decodedLen;

  if (index == 0 || otaNextChunkIndex == otaTotalChunks || (otaNextChunkIndex % 8) == 0) {
    Serial.printf("[OTA] chunk %u/%u written (%u bytes, total %u)\\n",
      otaNextChunkIndex, otaTotalChunks, (unsigned)decodedLen, (unsigned)otaWrittenBytes);
  }

  int progress = otaFirmwareSize > 0 ? (otaWrittenBytes * 100) / otaFirmwareSize : 0;
  if (otaNextChunkIndex % 4 == 0 || otaNextChunkIndex == otaTotalChunks) {
    publishOtaStatus(sessionId, "progress", progress);
  }
}

void handleOtaEnd(const char* sessionId) {
  Serial.println("[OTA] ota_end received");
  if (!otaActive || !sessionId || strcmp(sessionId, otaSessionId) != 0) {
    Serial.println("[OTA] end ignored (inactive or session mismatch)");
    return;
  }

  if (otaNextChunkIndex != otaTotalChunks || otaWrittenBytes != otaFirmwareSize) {
    Serial.printf("[OTA] incomplete transfer chunks=%u/%u bytes=%u/%u\\n",
      otaNextChunkIndex, otaTotalChunks, (unsigned)otaWrittenBytes, (unsigned)otaFirmwareSize);
    publishOtaStatus(sessionId, "error", -1, "Incomplete transfer");
    Update.abort();
    resetOtaSession();
    return;
  }

  if (Update.end(true) && Update.isFinished()) {
    Serial.println("[OTA] complete, restarting");
    publishOtaStatus(sessionId, "complete", 100);
    resetOtaSession();
    delay(1000);
    ESP.restart();
  } else {
    logOtaUpdateError("Update.end");
    publishOtaStatus(sessionId, "error", -1, "Update.end failed");
    Update.abort();
    resetOtaSession();
  }
}

void handleOtaAbort(const char* sessionId) {
  Serial.println("[OTA] ota_abort received");
  if (!otaActive || !sessionId || strcmp(sessionId, otaSessionId) != 0) return;
  Update.abort();
  publishOtaStatus(sessionId, "aborted");
  resetOtaSession();
}

void handleOtaChunkMessage(byte* payload, unsigned int length) {
  Serial.printf("[OTA] chunk MQTT payload=%u bytes\\n", length);
  DynamicJsonDocument doc(length + 128);
  DeserializationError error = deserializeJson(doc, payload, length);
  if (error) {
    Serial.print("[OTA] chunk JSON parse failed: ");
    Serial.println(error.c_str());
    return;
  }
  handleOtaChunk(
    doc["sessionId"].as<const char*>(),
    doc["index"].as<uint16_t>(),
    doc["data"].as<const char*>()
  );
}

void callback(char* topic, byte* payload, unsigned int length) {
  if (length > 12 && strstr((const char*)payload, "\\"ota_chunk\\"") != nullptr) {
    handleOtaChunkMessage(payload, length);
    return;
  }

  StaticJsonDocument<768> doc;
  DeserializationError error = deserializeJson(doc, payload, length);
  if (error) {
    Serial.print("[OTA] command JSON parse failed: ");
    Serial.println(error.c_str());
    return;
  }

  const char* cmd = doc["command"];
  if (!cmd) return;

  if (strcmp(cmd, "ota_begin") == 0) {
    handleOtaBegin(
      doc["sessionId"].as<const char*>(),
      doc["firmwareSize"].as<size_t>(),
      doc["totalChunks"].as<uint16_t>()
    );
    return;
  }
  if (strcmp(cmd, "ota_end") == 0) {
    handleOtaEnd(doc["sessionId"].as<const char*>());
    return;
  }
  if (strcmp(cmd, "ota_abort") == 0) {
    handleOtaAbort(doc["sessionId"].as<const char*>());
    return;
  }
  if (strcmp(cmd, "get_sensor_data") == 0) {
    int pin = doc["pin"];
    char wetKey[16], dryKey[16];
    sprintf(wetKey, "wet_%d", pin);
    sprintf(dryKey, "dry_%d", pin);
    float wetVal = preferences.getFloat(wetKey, 900.0);
    float dryVal = preferences.getFloat(dryKey, 2400.0);
    float rawReading = analogRead(pin);

    StaticJsonDocument<256> responseDoc;
    responseDoc["userId"] = "${userId}";
    responseDoc["deviceId"] = ${deviceId};
    responseDoc["deviceSecret"] = "${deviceSecret}";
    responseDoc["sensor_data_response"] = true;
    responseDoc["pin"] = pin;
    responseDoc["wetValue"] = wetVal;
    responseDoc["dryValue"] = dryVal;
    responseDoc["rawReading"] = rawReading;
    char responseBuffer[256];
    serializeJson(responseDoc, responseBuffer);
    client.publish("devices/${userId}/${deviceId}/data", responseBuffer);
  }
  else if (strcmp(cmd, "set_calibration") == 0) {
    int pin = doc["pin"];
    float wetValue = doc["wetValue"];
    float dryValue = doc["dryValue"];

    if (wetValue >= 0 && wetValue <= 4095 && dryValue >= 0 && dryValue <= 4095 && wetValue < dryValue) {
      char wetKey[16], dryKey[16];
      sprintf(wetKey, "wet_%d", pin);
      sprintf(dryKey, "dry_%d", pin);
      preferences.putFloat(wetKey, wetValue);
      preferences.putFloat(dryKey, dryValue);

      StaticJsonDocument<256> confirmDoc;
      confirmDoc["userId"] = "${userId}";
      confirmDoc["deviceId"] = ${deviceId};
      confirmDoc["deviceSecret"] = "${deviceSecret}";
      confirmDoc["calibration_saved"] = true;
      confirmDoc["sensor_pin"] = pin;
      char confirmBuffer[256];
      serializeJson(confirmDoc, confirmBuffer);
      client.publish("devices/${userId}/${deviceId}/data", confirmBuffer);
    }
  }
  else if (strcmp(cmd, "get_raw_reading") == 0) {
    int pin = doc["pin"];
    float rawReading = analogRead(pin);

    StaticJsonDocument<256> responseDoc;
    responseDoc["userId"] = "${userId}";
    responseDoc["deviceId"] = ${deviceId};
    responseDoc["deviceSecret"] = "${deviceSecret}";
    responseDoc["raw_reading_response"] = true;
    responseDoc["pin"] = pin;
    responseDoc["rawReading"] = rawReading;
    char responseBuffer[256];
    serializeJson(responseDoc, responseBuffer);
    client.publish("devices/${userId}/${deviceId}/data", responseBuffer);
  }
  else if (strcmp(cmd, "reset_safeguards") == 0) {
    // Reset all safeguards for water pumps
    safeguardsActive = false;
    pumpActivatedTime = 0;
    lastMoistureReading = -1.0;
    previousMoistureReading = -1.0;
    lastReadingTime = 0;

    StaticJsonDocument<256> responseDoc;
    responseDoc["userId"] = "${userId}";
    responseDoc["deviceId"] = ${deviceId};
    responseDoc["deviceSecret"] = "${deviceSecret}";
    responseDoc["safeguards_reset_response"] = true;
    char responseBuffer[256];
    serializeJson(responseDoc, responseBuffer);
    client.publish("devices/${userId}/${deviceId}/data", responseBuffer);
  }
}

void setup() {
  Serial.begin(115200);
  delay(500);
  Serial.println();
  Serial.println("Garden Sync booting...");

  // Initialize Preferences for ESP32
  preferences.begin("calibration", false); // Namespace: "calibration", read/write mode

  WiFi.mode(WIFI_STA);
  WiFi.begin(ssid, password);

  uint8_t tries = 0;
  while (WiFi.status() != WL_CONNECTED && tries < 30) {
    delay(500);
    Serial.print(".");
    tries++;
  }
  if (WiFi.status() == WL_CONNECTED) {
    Serial.println();
    Serial.println("Wi-Fi connected!");
    Serial.print("IP: "); Serial.println(WiFi.localIP());
  } else {
    Serial.println();
    Serial.println("Wi-Fi FAILED – will retry in loop");
  }

  client.setBufferSize(MQTT_RX_BUFFER_SIZE);
  client.setServer(mqtt_server, mqtt_port);
  client.setCallback(callback);
  Serial.printf("MQTT RX buffer: %u bytes\\n", MQTT_RX_BUFFER_SIZE);

  uint8_t mqttTries = 0;
  while (!client.connected() && mqttTries < 30) {
    if (client.connect("GardenNode${userId}-${deviceId}")) {
      Serial.println("MQTT connected");
      client.subscribe("devices/${userId}/${deviceId}/command");
      break;
    }
    delay(500);
    Serial.print(".");
    mqttTries++;
  }
  if (!client.connected()) {
    Serial.println();
    Serial.println("MQTT FAILED – will retry in loop");
  }

  ${sensorSetup.join('\n  ')}

  // LED strip setup
  ${ledStripSetup.join('\n  ')}

  // Pin modes for sensor pins
  ${sensors.filter(s => s.sensorType === 'soil_moisture').map(s => `pinMode(${s.pin}, INPUT);`).join('\n  ')}

  // NTP setup
  ${ntpSetup.join('\n  ')}

  // Pin modes for action pins
  ${actionPins.map(ap => `pinMode(${ap.pin}, OUTPUT);`).join('\n  ')}
}`;

  sensors.forEach((sensor, index) => {
    const key = sensor.dataType || sensor.sensorType;
    const group = sensorGroups[key];
    const suffix = group.length > 1 ? (group.findIndex(g => g.pin === sensor.pin) + 1) : '';
    const varName = `${key}${suffix}`;

    switch (sensor.sensorType) {
      case 'DHT11':
      case 'DHT22':
        const readMethod = sensor.dataType === 'temperature' ? 'readTemperature' : 'readHumidity';
        let readCode = `float ${varName} = dht${pinToDhtIdx[sensor.pin]}.${readMethod}();`;
        if (sensor.dataType === 'temperature' && sensor.unit === 'F') {
          readCode += `
  ${varName} = (${varName} * 9.0 / 5.0) + 32.0; // Convert to Fahrenheit`;
        }
        readCode += `
  if (isnan(${varName})) {
    ${varName} = -999.0; // Error value for invalid reading
    //Serial.println("Error reading ${sensor.dataType} from ${sensor.name || sensor.sensorType}");
  } // ${sensor.name || sensor.sensorType}`;
        sensorReads.push(readCode);
        break;
      case 'soil_moisture':
        let moistureCode = `float rawReading = analogRead(${sensor.pin});
  char wetKey[16], dryKey[16];
  sprintf(wetKey, "wet_%d", ${sensor.pin});
  sprintf(dryKey, "dry_%d", ${sensor.pin});
  float dryVal = preferences.getFloat(dryKey, 2400.0);
  float wetVal = preferences.getFloat(wetKey, 900.0);
  float ${varName} = map(rawReading, wetVal, dryVal, 100, 0);
  ${varName} = constrain(${varName}, 0, 100);

  // Always update moisture readings for safeguards logic
  unsigned long currentTime = millis();

  // Check for sudden reading jumps (>10% in 1 second) - only if safeguards not already active
  if (!safeguardsActive && lastMoistureReading >= 0 && currentTime - lastReadingTime < 1000) {
    float readingDiff = abs(${varName} - lastMoistureReading);
    if (readingDiff > 10.0) {
      safeguardsActive = true;
      Serial.println("SAFEGUARDS: Sudden moisture reading jump >10%");
    }
  }

  // Always update reading history for pump control logic
  previousMoistureReading = lastMoistureReading;
  lastMoistureReading = ${varName};
  lastReadingTime = currentTime;`;

        sensorReads.push(moistureCode);
        break;
      default:
        sensorReads.push(`float ${varName} = analogRead(${sensor.pin}); // ${sensor.name || sensor.sensorType}`);
        break;
    }
  });

  // Add safeguards logic for pump control
  if (hasSoilMoisture && hasPumps) {
    const waterPumpPins = actionPins.filter(ap => ap.type === 'water_pump');
    emergencyLogic.push(`
// Safeguards checks
if (safeguardsActive) {
  // Force all water pumps off during safeguards
  ${waterPumpPins.map(ap => `${ap.name.toLowerCase().replace(/\s+/g, '_')} = false;`).join('\n  ')}
} else {
  // Check if water pump was activated and moisture isn't rising
  bool pumpActive = ${waterPumpPins.map(ap => `${ap.name.toLowerCase().replace(/\s+/g, '_')}`).join(' || ')};

  if (pumpActive && pumpActivatedTime == 0) {
    pumpActivatedTime = millis();
  } else if (!pumpActive) {
    pumpActivatedTime = 0;
  }

  // Check for non-rising moisture after 15 seconds of pump activation
  if (pumpActivatedTime > 0 && millis() - pumpActivatedTime > 15000) { // 15 seconds
    if (lastMoistureReading >= 0 && previousMoistureReading >= 0) {
      if (lastMoistureReading <= previousMoistureReading + 2.0) { // Moisture not rising significantly
        safeguardsActive = true;
        Serial.println("SAFEGUARDS: Moisture not rising after 15s pump activation");
      }
    }
    pumpActivatedTime = 0; // Reset timer
  }
}`);
  }

  const reads = sensorReads.join('\n  ');

  // Generate checks (sensor-based and time-based automations)
  // Declare shared variables once at the beginning
  let sharedVars = [];
  if (automations.some(a => a.trigger.type === 'time')) {
    sharedVars.push('timeClient.update();\n  int currentMinutes = timeClient.getHours() * 60 + timeClient.getMinutes();');
  }

  const checks = automations.map(a => {
    const actionPinObj = actionPins.find(ap => ap.pin === a.action.pin);
    if (!actionPinObj) return '// Invalid action pin';
    const activeVar = actionPinObj.name.toLowerCase().replace(/\s+/g, '_');

    if (a.trigger.type === 'sensor') {
      const sensor = sensors.find(s => s.sensorID === a.trigger.sensor);
      if (!sensor) return '// Invalid sensor';
      const key = sensor.dataType || sensor.sensorType;
      const group = sensorGroups[key];
      const suffix = group.length > 1 ? (group.findIndex(g => g.pin === sensor.pin) + 1) : '';
      const varName = `${key}${suffix}`;
      let line = ``;
      if(a.action.type === 'Activate') {
          line += `if (${varName} ${a.trigger.operator} ${a.trigger.threshold}) ${activeVar} = true;`;
      } else if(a.action.type === 'Deactivate') {
          line += `if (${varName} ${a.trigger.operator} ${a.trigger.threshold}) ${activeVar} = false;`;
      }
      return line;
    } else if (a.trigger.type === 'time') {
      const [startHours, startMinutes] = a.trigger.startTime.split(':').map(Number);
      const [endHours, endMinutes] = a.trigger.endTime.split(':').map(Number);
      const startTotalMinutes = startHours * 60 + startMinutes;
      const endTotalMinutes = endHours * 60 + endMinutes;

      // Handle overnight time windows (start time > end time)
      const isOvernight = startTotalMinutes > endTotalMinutes;
      const timeCheck = isOvernight
        ? `currentMinutes >= ${startTotalMinutes} || currentMinutes <= ${endTotalMinutes}`
        : `currentMinutes >= ${startTotalMinutes} && currentMinutes <= ${endTotalMinutes}`;

      let line = ``;
      if(a.action.type === 'Activate') {
          line += `if (${timeCheck}) ${activeVar} = true;`;
      } else if(a.action.type === 'Deactivate') {
          line += `if (${timeCheck}) ${activeVar} = false;`;
      }
      return line;
    }
    return '// Invalid trigger type';
  }).join('\n  ');

  // Combine shared variables with checks
  const allChecks = sharedVars.length > 0 ? sharedVars.join('\n  ') + '\n  ' + checks : checks;

  // Generate dynamic JSON with all sensor data
  const jsonFields = sensors.map((sensor, index) => {
    const key = sensor.dataType || sensor.sensorType;
    const group = sensorGroups[key];
    const suffix = group.length > 1 ? (group.findIndex(g => g.pin === sensor.pin) + 1) : '';
    const varName = `${key}${suffix}`;
    const fieldName = getSensorMqttKey(sensor);
    return `
  if (${varName} >= 0) {
    doc["${fieldName}"] = ${varName};
  }`;
  }).join('\n  ');

  const loopLogic = `
  void loop() {
    if (!client.connected()) reconnect();
    client.loop();

    if (otaActive) {
      delay(5);
      return;
    }

    if(millis() - lastUpdateTime > 1000) { // Update every 1 seconds
      lastUpdateTime = millis();
      ${reads}
      ${emergencyLogic.join('\n  ')}
      ${allChecks}

      // Execute actions based on active states
      ${actionPins.map((ap, idx) => {
        let block = '';
        const activeVar = ap.name.toLowerCase().replace(/\s+/g, '_');
        if (ap.type === 'light' && ap.lightType === '3 Lead LED Strip') {
          const ledStripIndex = actionPins.filter(ap2 => ap2.type === 'light' && ap2.lightType === '3 Lead LED Strip').findIndex(ap2 => ap2.pin === ap.pin);
          if (ledStripIndex !== -1) {
            const ledVar = `leds${ledStripIndex}`;
            const numLights = ap.numLights;
            return `
      if (${activeVar}) {
        fill_solid(${ledVar}, ${numLights}, CRGB::White);
        FastLED.show();
      }`;
          }
        } else {
          if (ap.type === 'water_pump') {
            block += `
      if(!safeguardsActive && ${activeVar}) {
        digitalWrite(${ap.pin}, HIGH);
      } else {
        digitalWrite(${ap.pin}, LOW);
        ${activeVar} = false; // Reset active state if safeguards are active or pump should be off
      }`;
          } else {
            block += `
      if (${activeVar}) {
        digitalWrite(${ap.pin}, HIGH);
      } else {
        digitalWrite(${ap.pin}, LOW);
      }`;
        }
      }
        return block;
      }).join('\n    ')}

    // Send active states and emergency state in JSON
    StaticJsonDocument<512> doc;
    doc["userId"] = "${userId}";
    doc["deviceId"] = ${deviceId};
    doc["deviceSecret"] = "${deviceSecret}";
    ${actionPins.map((ap) => `doc["${getActionPinMqttKey(ap)}"] = (bool)${ap.name.toLowerCase().replace(/\s+/g, '_')};`).join('\n    ')}
    ${jsonFields}

    // Add safeguards status if applicable
    ${hasSoilMoisture && hasPumps ? `
    if (safeguardsActive != lastSafeguardsState) {
      doc["safeguards_active"] = safeguardsActive;
      lastSafeguardsState = safeguardsActive;
    }` : ''}

    char buffer[512];
    serializeJson(doc, buffer);
    client.publish("devices/${userId}/${deviceId}/data", buffer);
  }

  delay(100);
}`;

  const utils = `
void reconnect() {
  while (!client.connected()) {
    client.setBufferSize(MQTT_RX_BUFFER_SIZE);
    if (client.connect("GardenNode${userId}-${deviceId}")) {
      Serial.println("MQTT reconnected");
      client.subscribe("devices/${userId}/${deviceId}/command");
      break;
    } else {
      Serial.println("MQTT reconnect failed, retrying...");
      delay(2000);
    }
  }
}
`;
  return `${includes}\n\n${wifiSetup}\n\n${loopLogic}\n\n${utils}`;
}
